import { useEffect } from 'react'
import { gsap, Linear, Elastic } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { MorphSVGPlugin } from 'gsap/MorphSVGPlugin'
import { MotionPathPlugin } from 'gsap/MotionPathPlugin'
import { Physics2DPlugin } from 'gsap/Physics2DPlugin'

import style from './floaty.module.css'

function Floaty(props) {
  useEffect(() => {
    gsap.registerPlugin(MotionPathPlugin)
    gsap.registerPlugin(Physics2DPlugin)
    gsap.registerPlugin(MorphSVGPlugin)
    gsap.registerPlugin(ScrollTrigger)
    const xmlns = 'http://www.w3.org/2000/svg'
    const xlinkns = 'http://www.w3.org/1999/xlink'
    const select = (s) => document.querySelector(s)
    const dot = select('#dot')
    const pContainer = select('#pContainer')
    const particlePool = []
    const numParticles = 275
    let particleCount = 0

    gsap.fromTo(
      `.${style.cov1}`,
      {
        y: 35,
        opacity: 0,
      },
      {
        y: 30,
        opacity: 0.9,
        duration: 1,
        delay: 1,
        ease: Elastic.easeOut.config(1, 1),
      }
    )

    gsap.fromTo(
      `.${style.awslogo}`,
      {
        y: -80,
        opacity: 0,
      },
      {
        y: -30,
        opacity: 1,
        duration: 1,
        delay: 1,
        ease: Elastic.easeOut.config(1, 1),
      }
    )

    gsap.set('#pContainer', { mixBlendMode: 'overlay' })

    gsap.set(['#startShape', '#endShape'], {
      transformOrigin: '50% 50%',
    })
    const randomBetween = (min, max) => Math.floor(Math.random() * (max - min + 1) + min)

    const playParticle = () => {
      const p = particlePool[particleCount]
      const curr = { x: gsap.getProperty(dot, 'x'), y: gsap.getProperty(dot, 'y') }
      const rad = Math.atan2(curr.y - 300, curr.x - 400)
      const deg = Math.round(rad * (180 / Math.PI))
      gsap.set(p, {
        x: randomBetween(curr.x, curr.x - 5),
        y: randomBetween(curr.y, curr.y + 5),
        alpha: 1,
        transformOrigin: '50% 50%',
      })
      const tlpart = gsap.timeline({})
      tlpart.to(p, {
        // paused:true,
        duration: randomBetween(3, 6),
        physics2D: {
          velocity: randomBetween(5, 10),
          angle: deg % 2 ? deg : deg - 180, // randomBetween(-95, -85),
          gravity: randomBetween(-1, 1),
        },

        scale: randomBetween(0, 3.3),
        // rotation:randomBetween(180, 780),
        onComplete: completeParticle,
        onCompleteParams: [p],
        alpha: 0,
        // skewY:(Math.random() * 180),
        // ease: Power4.easeIn,
        // onStart:flicker,
        // onStartParams:[p]
      })

      particleCount += 1

      particleCount = particleCount >= numParticles ? 0 : particleCount
    }

    const mainTl = gsap.timeline({
      scrollTrigger: {
        trigger: `.${style.floatycircle}`,
        toggleActions: 'play pause resume pause',
        start: '-=100',
        end: 2000,
        id: 'floatyst',
      },
    })
    const tlpart1 = gsap.timeline({ repeat: -1, yoyo: false })
    const tlpart2 = gsap.timeline({ repeat: -1, yoyo: false, onUpdate: playParticle })
    tlpart2.to(dot, {
      duration: 6,
      motionPath: {
        path: '#orbit',
      },
      ease: Linear.easeNone,
    })

    // shapes
    tlpart1
      .to('#startShape', {
        rotation: 360,
        duration: 20,
        ease: Linear.easeNone,
      })
      .to(
        '#endShape',
        {
          duration: 20,
          rotation: -360,
          ease: Linear.easeNone,
        },
        '-=20'
      )

    const createParticles = () => {
      let i = numParticles
      let p
      // eslint-disable-next-line no-plusplus
      while (-1 < --i) {
        p = document.createElementNS(xmlns, 'use')
        p.setAttributeNS(xlinkns, 'xlink:href', '#particle')
        pContainer.appendChild(p)

        // p.setAttributeNS(null, 'fill', particleColorArray[i % particleColorArray.length]);
        p.setAttributeNS(null, 'opacity', 0)
        p.setAttribute('class', 'particle')
        particlePool.push(p)
      }
    }

    const completeParticle = (p) => {
      gsap.set(p, {
        x: -10,
        y: -10,
        alpha: 1,
        scale: 1,
      })
    }

    createParticles()
    mainTl.timeScale(0.9)
    mainTl.add(tlpart1, 0)
    mainTl.add(tlpart2, 0)

    return () => {
      ScrollTrigger.getById('floatyst').kill()
    }
  }, [])
  return (
    <svg viewBox="0 0 1600 1200" className={style.floatycircle} {...props}>
      <defs>
        <filter id="goo">
          <feGaussianBlur in="SourceGraphic" stdDeviation={8} result="blur" />
          <feColorMatrix in="blur" values="1 0 0 0 0 0 1 0 0 0 0 0 1 0 0 0 0 0 21 -9" result="cm" />
          <feBlend />
        </filter>
        <filter
          id="glow"
          x="-100%"
          y="-100%"
          width="250%"
          height="250%"
          colorInterpolationFilters="sRGB"
        >
          <feGaussianBlur stdDeviation={10} result="coloredBlur" />
          <feOffset result="offsetblur" />
          <feFlood id="glowAlpha" floodColor="#43598F" floodOpacity={1} />
          <feComposite in2="offsetblur" operator="in" />
          <feMerge>
            <feMergeNode />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
        <filter
          id="blur"
          x="-100%"
          y="-100%"
          width="250%"
          height="250%"
          colorInterpolationFilters="sRGB"
        >
          <feGaussianBlur stdDeviation={3} />
          <feMerge>
            <feMergeNode />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
        <circle id="particle" cx={0} cy={0} r={1} fill="#FFF" />
      </defs>
      <radialGradient id="dotGrad" cx={0} cy={0} r={24} gradientUnits="userSpaceOnUse">
        <stop offset={0} stopColor="#FFF" />
        <stop offset={0.3} stopColor="#1DECB6" stopOpacity={0.6} />
        <stop offset={0.8} stopColor="#081029" stopOpacity={0} />
      </radialGradient>
      <g stroke="none" strokeWidth={1} fill="none" fillRule="evenodd" className={style.awslogo}>
        <path
          d="M37.703 14.699c.437.273 1.093.491 1.912.655.82.219 1.694.274 2.568.274.875 0 1.64-.11 2.35-.328a6.576 6.576 0 001.858-.93c.492-.382.929-.874 1.202-1.475a4.529 4.529 0 00.437-1.967c0-.874-.273-1.639-.765-2.35-.492-.655-1.366-1.201-2.568-1.584l-2.35-.765c-.874-.273-1.53-.6-1.858-.929a1.607 1.607 0 01-.491-1.202c0-.656.273-1.202.765-1.475.546-.328 1.256-.492 2.24-.492 1.202 0 2.24.219 3.224.656.273.109.492.218.656.218.273 0 .382-.164.382-.546v-.656c0-.218-.055-.437-.164-.546A1.29 1.29 0 0046.61.82a2.143 2.143 0 00-.71-.274c-.273-.109-.601-.164-.929-.273C44.642.22 44.26.11 43.877.11 43.495 0 43.112 0 42.73 0c-.765 0-1.42.11-2.077.273-.655.219-1.256.492-1.748.874-.492.383-.874.82-1.202 1.367-.328.546-.437 1.147-.437 1.857 0 .875.273 1.694.82 2.405.546.765 1.42 1.311 2.677 1.693l2.404.765c.82.274 1.366.547 1.694.875.328.328.437.71.437 1.147 0 .656-.273 1.202-.874 1.585-.601.382-1.366.546-2.46.546a8.045 8.045 0 01-1.966-.218c-.656-.11-1.257-.328-1.858-.601a3.765 3.765 0 01-.437-.164c-.11-.055-.219-.055-.328-.055-.273 0-.383.164-.383.546v.82c0 .164.055.328.11.492.164.218.328.328.6.492M9.453 6.612c-.601-.11-1.202-.11-1.749-.11-1.693 0-3.06.437-4.098 1.312-1.038.874-1.53 2.021-1.53 3.442 0 1.366.383 2.404 1.202 3.224.82.82 1.913 1.202 3.334 1.202 1.967 0 3.606-.765 4.917-2.295.164.383.328.71.492.984.164.273.383.546.601.765a.667.667 0 00.492.218c.11 0 .273-.054.437-.164l1.038-.71c.219-.164.328-.328.328-.492 0-.109-.054-.218-.11-.382-.218-.437-.436-.82-.546-1.202-.109-.383-.163-.875-.163-1.476V5.464c0-1.803-.438-3.17-1.366-4.098C11.802.437 10.382 0 8.36 0c-.929 0-1.803.11-2.623.328-.765.218-1.475.437-2.076.765-.219.11-.383.218-.437.328-.11.109-.11.328-.11.6v.82c0 .328.11.547.328.547.055 0 .164 0 .274-.055.109-.055.327-.11.6-.218.602-.219 1.203-.438 1.804-.547a6.788 6.788 0 011.803-.218c1.311 0 2.186.273 2.732.765.547.491.765 1.42.765 2.677v1.202a30.97 30.97 0 00-1.967-.382zm1.912 2.95c0 .547-.054 1.038-.163 1.421-.11.382-.274.765-.547 1.038-.437.492-.983.874-1.584 1.038a5.183 5.183 0 01-1.749.328c-.765 0-1.366-.218-1.749-.6-.382-.383-.6-.984-.6-1.75 0-.819.273-1.475.819-1.912.546-.437 1.366-.656 2.459-.656.492 0 1.038.055 1.584.11.547.054 1.039.164 1.53.273v.71z"
          fill="#FFF"
          transform="translate(1 1) translate(360 268)"
        />
        <path
          d="M19.78 14.258c.11.327.219.546.328.655.11.11.328.164.656.164h1.42c.328 0 .547-.054.656-.164.11-.109.219-.328.328-.655l2.568-10.765 2.623 10.765c.055.327.164.546.328.655.11.11.382.164.656.164h1.42c.273 0 .492-.054.656-.164.11-.109.273-.328.328-.655L35.79 1.362c.055-.164.11-.328.11-.382 0-.11.054-.164.054-.274 0-.273-.11-.382-.382-.382h-1.53c-.328 0-.547.055-.656.164-.11.11-.219.328-.328.656l-2.896 11.201L27.485 1.09C27.43.76 27.32.543 27.157.433c-.11-.11-.383-.164-.656-.164h-1.366c-.328 0-.546.055-.656.164-.109.11-.218.328-.327.656l-2.623 11.038-2.787-11.038c-.11-.328-.218-.546-.328-.656-.109-.11-.328-.164-.655-.164h-1.64c-.273 0-.382.11-.382.383 0 .11.054.328.164.655l3.88 12.95"
          fill="#FFF"
          transform="translate(1 1) translate(360 268) translate(0 .168)"
        />
        <path
          d="M44.533 22.345c-6.12 2.622-12.84 3.88-18.906 3.88-9.016 0-17.759-2.46-24.807-6.613-.601-.382-1.093.274-.547.765 6.557 5.902 15.19 9.453 24.808 9.453 6.83 0 14.807-2.13 20.326-6.229.82-.655.055-1.639-.874-1.256"
          fill="#F90"
          transform="translate(1 1) translate(360 268) translate(0 .168)"
        />
        <path
          d="M41.09 20.217c-.491.328-.382.82.11.765 1.748-.218 5.737-.71 6.393.219.71.874-.765 4.59-1.42 6.284-.22.492.218.71.71.328 2.95-2.46 3.715-7.65 3.114-8.36-.6-.82-5.792-1.421-8.906.764"
          fill="#F90"
          transform="translate(1 1) translate(360 268)"
        />
      </g>
      <text
        className={style.cov1}
        fill="#fff"
        x="272"
        y="290"
        stroke="none"
        strokeWidth={1}
        fillRule="evenodd"
        fontFamily="GeomanistUltra, Geomanist"
        fontSize={48}
        fontWeight={800}
      >
        <tspan>24/7 MSSP</tspan>
      </text>
      <g id="shapeGroup" filter="url(#glow)">
        <path
          id="startShape"
          className="shape"
          fill="none"
          stroke="#04EAAE"
          strokeWidth={5}
          strokeMiterlimit={10}
          d="M570.1 305c2 17-10.8 21.3-14.1 38-1.9 17.1 7.8 45.5 1.6 59.4-5.9 14.6-29.8 11.8-45.5 25.3-7 6-14.6 19-23.8 28.6-9.1 9.9-19.9 16.3-31.8 17.7-21.1 3.2-33.6-8.1-55.8-8.4-21.9 1.8-53.9 11.2-70.2 8.5-17.2-1.2-15.5-21.3-31.4-30.7-16.1-8.4-51.4-8.8-60.8-24.5-9.7-15.1 3.9-35-1.6-53.6-2.4-8.1-10.1-19.3-14.5-29.9-4.8-10.7-6.4-20.9-6.3-30.1.3-8.6 3-14.5 8.3-20.5 5-6 12.7-12.1 14.9-20.9 5.2-18.1-1.3-49.5 9.4-63.8 10.4-14.6 32.6-10 47.3-22.6 6.7-5.6 14.5-17.6 23-26 8.5-8.8 17.9-13.9 27.1-15.1 16.1-3 23.2 7.5 40.1 8 16.5-1.7 41.3-10.6 53-8 12.4.6 12.8 18.9 27.9 27.9 14.9 7.9 45.8 6.3 59.5 21.4 13.8 14.9 5.5 37.5 14.7 56.6 4 8.5 13.7 20.3 19.6 31.6 6.6 11 9.1 21.8 9.4 31.1z"
        />
        <path
          id="orbit"
          fill="none"
          stroke="none"
          strokeWidth={4}
          strokeMiterlimit={10}
          d="M561.3 302.1c.6 10-2.4 23.7-4.8 34.9-2.3 11.5-1.5 21.5-7.3 33.3-5.8 12.1-21 23.2-32 33.5-11 10.4-16.6 22.9-29.5 33.1-12.7 10-33.1 15.2-49.4 21.8-15.7 6.8-28.2 14.7-45.2 15.6-16.9 1.5-37.1-1-53.9-2.6-17.2-1.7-33.1.7-48.4-5-15.6-5.8-25.8-23-34.4-34.3-8.8-11.3-18.2-16.4-22.5-29.1-4.1-12.5-3.3-32.5-4.9-48.6-1.7-15.6-4.6-27.5-4.2-46.9-.2-19.3 5.3-44.9 12.1-65.7 6.5-21.4 10.5-41.9 23.2-57.9 13.1-16.3 33-23.7 45.4-30.4 12.6-6.9 19.2-13.5 28.3-15.4 8.9-1.9 21.3-.3 31.3-.7 9.6-.4 15.5-2.4 27.9-1.5 12.5.5 30.9 6.7 45.8 13.5 15.3 6.6 30 10.8 44 22.6 14.3 12 24 30.7 33 43.2 9.2 12.7 19.7 20.1 26 29.8 6.1 9.6 7.4 22.1 10.8 31.7 3.6 9.3 8.6 15.1 8.7 25.1z"
        />
        <path
          id="endShape"
          className="shape"
          fill="none"
          stroke="#1DECB6"
          strokeWidth={5}
          strokeMiterlimit={10}
          d="M565.2 305.1c.2 14.1-7.5 31.8-11.4 46.5-3.3 15.3 1.5 30-4.3 44-5.6 14.5-25.8 22.8-37.4 32.9-11.5 10.5-14.5 26.1-27.8 31.9-13 5.9-34.7 2.5-50.4 5.5-15.3 3.5-25.3 11.3-40.8 11.1-15.7 1.4-36.2-6.5-52.8-11.1-17.2-3.9-34.7 1.6-49.8-6.1-15.4-7.8-22.2-30.4-31.5-42.6-9.8-12.3-23.3-16.1-27-28.7-3.9-12.2-.7-32.1-2.6-46.6-2.4-14-8.4-22.2-8.4-37-1.6-15.1 6.5-35.7 11.9-51.9 4.7-16.8.6-34.6 9.4-49.6 8.9-15.4 31.2-21.7 43.8-31.2 12.6-10 17.7-25.1 29.9-29.4 11.9-4.5 30.3 0 43.6-1.9 13-2.5 20.7-9 33.6-7.9 13.1-.4 30.6 7.2 44.7 11.6 14.7 3.8 29.2-.5 43 6.2 14.3 6.5 22.5 26.9 32.9 38.8 10.8 11.8 27.5 15.7 34.3 28.8 6.8 12.9 2.9 33.6 6.1 48.4 3.7 14.5 12.3 24.3 11 38.3z"
        />
      </g>
      <circle id="dot" fill="url(#dotGrad)" cx={0} cy={0} r={24} />
      <g id="pContainer" />
    </svg>
  )
}

export default Floaty
